import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import {
  ALLOWED_PATHS,
  COOKIE_KEYS,
  MG_CONSUMER_LOGIN_ROUTE,
  MG_PAYMENT_PORTAL_SESSION_KEY,
  getPostSignOutLoginPath,
  isAuthenticatedFromCookies,
} from "./constatnts";
import {
  useNavigate,
  Routes,
  Route,
  useParams,
  useLocation,
  Navigate,
} from "react-router-dom";
import * as API from "./api";
import Emitter from "./event-emitter";
import { Cookies } from "react-cookie";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import companyData from "../assets/styles/theme.json";
import queryString from "query-string";
import { useAuth } from "../Context/AuthContext";
import { useOrganization } from "../Context/OrganisationContext";
import { showToast } from "../ui/SuccessModal";

// Lazy load components

const SignOut = lazy(() => import("../components/Sessions/SignOut"));
const SVGSprite = lazy(() => import("../components/SVGSprite"));

// Admin Portal Screens
const AdminAccountAddEditView = lazy(
  () => import("../screens/AdminPortal/AdminAccountAddEditView")
);
const AdminAccounts = lazy(
  () => import("../screens/AdminPortal/AdminAccounts")
);
const AdminAccountsView = lazy(
  () => import("../screens/AdminPortal/AdminAccountsView")
);
const AppConfiguration = lazy(() => import("../screens/AppConfiguration"));
const AppThirdPartyConfig = lazy(
  () => import("../screens/AppThirdPartyConfig")
);
const AdminAdmins = lazy(() => import("../screens/AdminPortal/AdminAdmins"));
const AdminOffers = lazy(() => import("../screens/AdminPortal/AdminOffers"));
const AdminPendingActions = lazy(
  () => import("../screens/AdminPortal/AdminPendingActions")
);
const AdminPortal = lazy(() => import("../screens/AdminPortal/AdminPortal"));
const AdminViewAddressDoc = lazy(
  () => import("../screens/AdminPortal/AdminViewAddressDoc")
);
const CompanyAccounts = lazy(
  () => import("../screens/AdminPortal/CompanyAccounts")
);
const ViewConsumerConversation = lazy(
  () => import("../screens/AdminPortal/ViewConsumerConversation")
);
const ViewPendingAddressUser = lazy(
  () => import("../screens/AdminPortal/ViewPendingAddressUser")
);
const ConsumerPaymentPlanDetails = lazy(
  () => import("../screens/ConsumerPortal/Payment/ConsumerPaymentPlanDetails")
);
const ConsumerAccountsPaymentplansReview = lazy(
  () => import("../screens/ConsumerPortal/ConsumerAccountsPaymentplansReview")
);

// Clients Portal Screens
const ClientsAccountAddEditView = lazy(
  () => import("../screens/ClientsPortal/ClientsAccountAddEditView")
);
const ClientsAccounts = lazy(
  () => import("../screens/ClientsPortal/AccountList")
);

// Deprecated: Keeps this commented for the old design reference
// const ClientsAccountsView = lazy(
//   () => import("../screens/ClientsPortal/ClientsAccountsView")
// );

const ClientAccountView = lazy(() => import("../screens/Accounts"));

const ClientsAddInvoices = lazy(
  () => import("../screens/ClientsPortal/ClientsAddInvoices")
);
const ClientsAdmins = lazy(
  () => import("../screens/ClientsPortal/ClientsAdmins")
);
// const ClientsDashboard = lazy(() =>
//   import("../screens/ClientsPortal/ClientsDashboard")
// );
const ClientsDashboard = lazy(() => import("../pages/AdminDashboard"));
const RecurringApprovals = lazy(() => import("../screens/RecurringApprovals"));
const RecurringHistory = lazy(() => import("../screens/RecurringHistory"));
const ClientsEditSettings = lazy(
  () => import("../screens/ClientsPortal/ClientsEditSettings")
);
const ClientsEmployeeAccountsView = lazy(
  () => import("../screens/ClientsPortal/ClientsEmployeeAccountsView")
);
const ClientsEmployees = lazy(
  () => import("../screens/ClientsPortal/ClientsEmployees")
);
const ClientsHelp = lazy(() => import("../screens/ClientsPortal/ClientsHelp"));
const ClientsHistory = lazy(
  () => import("../screens/ClientsPortal/ClientsHistory")
);
const ClientsInvoices = lazy(
  () => import("../screens/ClientsPortal/ClientsInvoices")
);
const ClientsInvoicesView = lazy(
  () => import("../screens/ClientsPortal/ClientsInvoicesView")
);
const ClientsPaymentPlanView = lazy(
  () => import("../screens/ClientsPortal/ClientsPaymentPlansView")
);
const ClientsAddCreditMemo = lazy(
  () => import("../screens/ClientsPortal/CreditMemo/ClientsAddCreditMemo")
);
const ClientsCreditMemoView = lazy(
  () => import("../screens/ClientsPortal/CreditMemo/ClientsCreditMemoView")
);
const ClientsOffers = lazy(
  () => import("../screens/ClientsPortal/ClientsOffers")
);
const ClientsPendingActions = lazy(
  () => import("../screens/ClientsPortal/ClientsPendingActions")
);
const AccountSwitch = lazy(
  () => import("../screens/ClientsPortal/AccountSwitch")
);
const ClientsPortal = lazy(
  () => import("../screens/ClientsPortal/ClientsPortal")
);
const ClientsProfile = lazy(
  () => import("../screens/ClientsPortal/ClientsProfile")
);
const ClientsReports = lazy(
  () => import("../screens/ClientsPortal/ClientsReports")
);
const ClientsReportView = lazy(
  () => import("../screens/ClientsPortal/ClientsReportView")
);
const ClientsSettings = lazy(
  () => import("../screens/ClientsPortal/ClientsSettings")
);
const ClientsTasks = lazy(
  () => import("../screens/ClientsPortal/ClientsTasks")
);
const ClientViewAddressDoc = lazy(
  () => import("../screens/ClientsPortal/ClientViewAddressDoc")
);
const MailchimpHistory = lazy(
  () => import("../screens/ClientsPortal/history/MailchimpHistory")
);
const MessageHistory = lazy(
  () => import("../screens/ClientsPortal/history/MessageHistory")
);
const ViewConsumerChat = lazy(
  () => import("../screens/ClientsPortal/history/ViewConsumerChat")
);
const ImportInvoiceData = lazy(
  () => import("../screens/ClientsPortal/importInvoiceData")
);
const LobHistory = lazy(() => import("../screens/ClientsPortal/LobHistory"));
const SkipPayment = lazy(() => import("../screens/ClientsPortal/SkipPayment"));
const SkipTrace = lazy(() => import("../screens/ClientsPortal/SkipTrace"));
const ConsumerAccounts = lazy(
  () => import("../screens/ConsumerPortal/ConsumerAccounts")
);
const ConsumerAccountsView = lazy(
  () => import("../screens/ConsumerPortal/ConsumerAccountsView")
);
const ConsumerDocumentValidate = lazy(
  () => import("../screens/ConsumerPortal/ConsumerDocumentValidate")
);
const ConsumerHelp = lazy(
  () => import("../screens/ConsumerPortal/ConsumerHelp")
);
const ConsumerPortal = lazy(
  () => import("../screens/ConsumerPortal/ConsumerPortal")
);
const ConsumerProfile = lazy(
  () => import("../screens/ConsumerPortal/ConsumerProfile")
);
const DebtDetail = lazy(() => import("../screens/ConsumerPortal/DebtDetail"));
const ConsumerAccountsNegotiateOneTimePayment = lazy(
  () =>
    import(
      "../screens/ConsumerPortal/Payment/ConsumerAccountsNegotiateOneTimePayment"
    )
);
const ConsumerAccountsNegotiatePaymentPlan = lazy(
  () =>
    import(
      "../screens/ConsumerPortal/Payment/ConsumerAccountsNegotiatePaymentPlan"
    )
);
const ConsumerAccountsPaymentNegotiateSelection = lazy(
  () =>
    import(
      "../screens/ConsumerPortal/Payment/ConsumerAccountsPaymentNegotiateSelection"
    )
);
const ConsumerAccountsPaymentSettleAccount = lazy(
  () =>
    import(
      "../screens/ConsumerPortal/Payment/ConsumerAccountsPaymentSettleAccount"
    )
);
const ConsumerAccountsPaymentValidate = lazy(
  () =>
    import("../screens/ConsumerPortal/Payment/ConsumerAccountsPaymentValidate")
);

// Lazy load components for error pages
const BadRequest = lazy(() => import("../components/error-pages/bad-request"));
const EmailVerifyError = lazy(
  () => import("../components/error-pages/emailVerifyError")
);
const ServerError = lazy(
  () => import("../components/error-pages/internal-server-error")
);

// Lazy load screens for Clients Portal SSO
const ClientsSSODashboard = lazy(
  () => import("../screens/ClientsPortal/sso/ClientsSSODashboard")
);
const CreateSubDomain = lazy(
  () => import("../screens/ClientsPortal/sso/CreateSubDomain")
);
const CustomizeWorkFlow = lazy(
  () => import("../screens/ClientsPortal/sso/CustomizeWorkFlow")
);

// Lazy load static pages under Clients Portal SSO
const ConsumerAccountsInvoices = lazy(
  () => import("../screens/ClientsPortal/sso/static-pages/ConsumerAccounts")
);
const ConsumerAccountsViewStatic = lazy(
  () => import("../screens/ClientsPortal/sso/static-pages/ConsumerAccountsView")
);
const InvoiceAmountFormStatic = lazy(
  () => import("../screens/ClientsPortal/sso/static-pages/InvoiceAmountForm")
);
const PaymentPortalLogin = lazy(
  () => import("../screens/ClientsPortal/sso/static-pages/PaymentPortalLogin")
);
const WelcomeScreenView = lazy(
  () => import("../screens/ClientsPortal/sso/WelcomeScreenView")
);

// Lazy load static screens for Consumer Portal
const ConsumerPortalStatic = lazy(
  () => import("../screens/ConsumerPortal/ConsumerPortalStatic")
);
const PayInvoices = lazy(() => import("../screens/ConsumerPortal/PayInvoices"));
const PayPaymentPlans = lazy(
  () => import("../screens/ConsumerPortal/PayPaymentPlans")
);

// Employee Portal Screens
const EmployeeAccounts = lazy(
  () => import("../screens/EmployeePortal/EmployeeAccounts")
);
const EmployeeAccountAddEditView = lazy(
  () => import("../screens/EmployeePortal/EmployeeAccountsAddEditView")
);
const EmployeeAccountsView = lazy(
  () => import("../screens/EmployeePortal/EmployeeAccountsView")
);
const EmployeeLobHistory = lazy(
  () => import("../screens/EmployeePortal/EmployeeLobHistory")
);
const EmployeeOffers = lazy(
  () => import("../screens/EmployeePortal/EmployeeOffers")
);
const EmployeePaymentHistory = lazy(
  () => import("../screens/EmployeePortal/EmployeePaymentHistory")
);
const EmployeePortal = lazy(
  () => import("../screens/EmployeePortal/EmployeePortal")
);
const EmployeeProfile = lazy(
  () => import("../screens/EmployeePortal/EmployeeProfile")
);
const EmployeeSkipTrace = lazy(
  () => import("../screens/EmployeePortal/EmployeeSkipTrace")
);
const EmployeeViewAddressDoc = lazy(
  () => import("../screens/EmployeePortal/EmployeeViewAddressDoc")
);
const EmployeeMailchimpHistory = lazy(
  () => import("../screens/EmployeePortal/history/EmployeeMailchimpHistory")
);
const EmployeeMessageHistory = lazy(
  () => import("../screens/EmployeePortal/history/EmployeeMessageHistory")
);
const EmployeeViewConsumerChat = lazy(
  () => import("../screens/EmployeePortal/history/EmployeeViewConsumerChat")
);

// Landing and Marketing Screens
const Landing = lazy(() => import("../screens/Landing"));
const ModalConductor = lazy(
  () => import("../components/App/Modals/ModalConductor")
);
const MakeGoodPolicy = lazy(() => import("../screens/MakeGoodPolicy"));
const MarketingContact = lazy(() => import("../screens/MarketingContact"));
const MarketingContent = lazy(() => import("../screens/MarketingContent"));
const MarketingHomepage = lazy(() => import("../screens/MarketingHomepage"));
const MarketingPrivacy = lazy(() => import("../screens/MarketingPrivacy"));
const MarketingTerms = lazy(() => import("../screens/MarketingTerms"));
const ConfirmPassword = lazy(
  () => import("../screens/NewLanding/ConfirmPassword")
);
const LandingHeader = lazy(() => import("../screens/NewLanding/LandingHeader"));
const NewRegistration = lazy(
  () => import("../screens/NewLanding/NewRegistration")
);
const PasswordSuccess = lazy(
  () => import("../screens/NewLanding/PasswordSuccess")
);
const OnboardingCompletion = lazy(
  () => import("../screens/Onboarding/OnboardingCompletion")
);
const OnboardingConfirmAccountCode = lazy(
  () => import("../screens/Onboarding/OnboardingConfirmAccountCode")
);
const OnboardingConfirmAccountPhone = lazy(
  () => import("../screens/Onboarding/OnboardingConfirmAccountPhone")
);
const OnboardingCreateAccount = lazy(
  () => import("../screens/Onboarding/OnboardingCreateAccount")
);
const OnboardingResidence = lazy(
  () => import("../screens/Onboarding/OnboardingResidence")
);
const OnboardingUserAgreement = lazy(
  () => import("../screens/Onboarding/OnboardingUserAgreement")
);
const OnboardingVerifyIdentity = lazy(
  () => import("../screens/Onboarding/OnboardingVerifyIdentity")
);
const ConsumerGetHelp = lazy(
  () => import("../screens/QuickBook/ConsumerGetHelp")
);
const ExpressPaymentFailed = lazy(
  () => import("../screens/QuickBook/ExpressPaymentFailed")
);
const ExpressPaymentSuccess = lazy(
  () => import("../screens/QuickBook/ExpressPaymentSuccess")
);
const InvoiceAmountForm = lazy(
  () => import("../screens/QuickBook/InvoiceAmountForm")
);
const DepositExpress = lazy(() => import("../screens/DepositExpress"));
const QbDisconnect = lazy(() => import("../screens/QuickBook/QbDisconnect"));
const QbFailure = lazy(() => import("../screens/QuickBook/QbFailure"));
const QbSuccess = lazy(() => import("../screens/QuickBook/QbSuccess"));
const SSOFailure = lazy(() => import("../screens/QuickBook/SSOFailure"));
const SSOSuccess = lazy(() => import("../screens/QuickBook/SSOSuccess"));
const StripePaymentFailed = lazy(
  () => import("../screens/QuickBook/StripePaymentFailed")
);
const StripePaymentSuccess = lazy(
  () => import("../screens/QuickBook/StripePaymentSuccess")
);
const ChoosePassword = lazy(() => import("../screens/Sessions/ChoosePassword"));
const Login = lazy(() => import("../screens/Sessions/Login"));
const ConsumerLogin = lazy(() => import("../screens/Sessions/ConsumerLogin"));
const ResetPassword = lazy(() => import("../screens/Sessions/ResetPassword"));

// Super Admin Portal Screens
const SuperAdminAccountAddEditView = lazy(
  () => import("../screens/SuperAdminPortal/SuperAdminAccountAddEditView")
);
const SuperAdminAccounts = lazy(
  () => import("../screens/SuperAdminPortal/SuperAdminAccounts")
);
const SuperAdminAccountsView = lazy(
  () => import("../screens/SuperAdminPortal/SuperAdminAccountsView")
);
const SuperAdminProfile = lazy(
  () => import("../screens/SuperAdminPortal/SuperAdminProfile")
);
const SuperAdminPortal = lazy(
  () => import("../screens/SuperAdminPortal/SuperAdminPortal")
);
const MakeGoodEULA = lazy(() => import("../screens/EndUserLicenseAgreement"));

const ElavonScreen = lazy(
  () => import("../screens/RecurringApprovals/component/elavon")
);

const PaymentsDeposit = lazy(() => import("../screens/PaymentsDeposit"));
const AddPaymentDeposit = lazy(() => import("../screens/AddPaymentDeposit"));
const PaymentDepositDetail = lazy(
  () => import("../screens/PaymentDepositDetail")
);
const Payouts = lazy(() => import("../screens/Payouts"));
const PayoutDetail = lazy(() => import("../screens/Payouts/PayoutDetail"));

// Data
const UserData = lazy(() => import("../screens/UserData"));
import userDashboardObjectLists from "../testData/userDashboardObjectLists.json";
import accountViewData from "../testData/accountViewData.json";
import userData from "../testData/userData.json";
import { useModal } from "../Context/ModalContext";
import Layout from "../components/Layout";
import Loader from "../ui/Loader";

// Fallback loading component
// const Loading = () => <div>Loading...</div>;

const AppRouter = (props) => {
  const navigate = useNavigate();
  // const { cookies } = props;
  const cookies = new Cookies();
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  const currentPath = window.location.pathname;
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const { id } = useParams();
  const { authState, handleUpdateAuthState } = useAuth();
  const { appData } = useOrganization();
  const { currentModal, showModal } = useModal();

  const appEmail = authState?.appEmail;
  // Stable reference: child effects that depend on `user` must not re-run every AppRouter render.
  const user = useMemo(
    () => ({
      is_onboarding_completed: false,
      ...authState?.user,
    }),
    [authState?.user]
  );
  const isLoggedIn = authState?.isLoggedIn;

  const hasRequiredRole = (requiredRoles) => {
    if (!requiredRoles || !requiredRoles.length) return true;

    return requiredRoles.some((role) => {
      switch (role) {
        case "client_admin":
          return user.client_admin === true;
        case "is_employee":
          return user.is_employee === true;
        case "super_admin":
          return user.super_admin === true;
        case "admin":
          return user.admin === true;
        default:
          return false;
      }
    });
  };

  const isRestrictedFromCompanySettings = () => {
    return user.client_admin === false && user.is_employee === true;
  };

  const [state, setState] = useState({
    back_Path: "",

    progressPercent: 0,
    appName: "", // company_name
    clientName: "",
    appData: {},
    userData: {},
    companyData: {},
    appThemeData: {},
    //TODO
    showModal: false,
    currentModal: null,
    optionalModalProps: null,

    confirmOnboardingPhoneNumber: false,
    intercomAppId: null,
    stripePubKey: null,
    isEULAtrue: false,

    prevPath: null, //useless
    hasAnyError: false, //useless
    heapEmail: null, //useless
    heapAppId: null, //useless
  });

  const isAuthenticated = isAuthenticatedFromCookies(cookies);

  const [globalLoading, setGlobalLoading] = useState(true);

  const getOrganizationData = () => {
    API.getOrganizationData()
      .then((data) => {
        if (data) {
          localStorage.setItem("themeData", JSON.stringify(data));
          setState({
            ...state,
            appName: data.name,
            clientName: data.clientName,
            companyData: data,
          });
        }
      })
      .catch((error) => {
        console.log("Error fetching organization data: ", error);
      });
  };

  // could be used as getOrganizationalDetails
  const getOrganizationalDetails = () => {
    // const cookies = new Cookies();
    let authuser = cookies.get("accessToken", { path: "/", secure: true });
    let orgKey = state.companyData && state.companyData.organization_key;
    if (!authuser || !orgKey) {
      // Don't call API if required headers are missing
      return Promise.resolve();
    }
    let user = {
      user_auth_token: authuser,
      organization_key: orgKey,
    };
    return API.getOrgDetails(user).then((data) => {
      if (data && data.status_code == 200) {
        setState((state) => ({
          ...state,
          progressPercent: parseFloat(
            data.data.organization.onboarding_percentage
          ),
        }));
      }
    });
  };

  const getFullUserProfile = (user) => {
    if (
      user.hasOwnProperty("user_id") &&
      user.user_id &&
      !user.admin &&
      !user.super_admin &&
      !user.client_admin
    ) {
      API.getUser(user).then((data) => {
        if ((typeof data != "undefined" || data != null) && !data.errors) {
          const consolidatedUser = Object.assign(user, data.user);
          setState((state) => ({
            ...state,
            user: consolidatedUser,
          }));
        } else {
          setState((state) => ({
            ...state,
            hasAnyError: true,
          }));
        }
      });
    }
  };

  const getAppData = async () => {
    try {
      const data = await API.getAppInfo();
      if (data && !data.errors) {
        if (data.appData) {
          setState((prevState) => ({
            ...prevState,
            appEmail: data.appData.company_email,
            appData: data.appData,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching app data:", error);
    }
  };

  const getAppConfigInfo = async () => {
    const newOrgData = JSON.parse(localStorage.getItem("themeData"));
    const orgKey = newOrgData?.organization_key || companyData.organization_key;

    try {
      const data = await API.getAppConfigInfo(orgKey);

      if (data && !data.errors && data.appConfigData) {
        const updatedState = { loading: false };

        data.appConfigData.forEach((config) => {
          switch (config.name) {
            case "HEAP":
              updatedState.heapEmail = config.email;
              updatedState.heapAppId = config.app_id;
              break;
            case "INTERCOM":
              updatedState.intercomAppId = config.app_id;
              break;
            case "STRIPE":
              updatedState.stripePubKey =
                process.env.REACT_APP_STRIPE_PUBLIC_KEY;
              break;
            default:
              break;
          }
        });

        setState((prevState) => ({
          ...prevState,
          ...updatedState,
        }));
        return true;
      } else {
        setState((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return false;
      }
    } catch (error) {
      console.error("Error fetching app config:", error);
      setState((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return false;
    }
  };

  const updateOnboardingComplete = () => {
    handleUpdateAuthState({
      user: {
        ...user,
        is_onboarding_completed: true,
      },
    });
  };

  const handleStateChange = (backpath) => {
    setState((prevState) => ({
      ...prevState,
      back_Path: backpath,
    }));
  };

  const handleModalChange = (showModalState, modalType, optionalModalProps) => {
    setState((prevState) => ({
      ...prevState,
      showModal: showModalState,
      currentModal: modalType,
      optionalModalProps: optionalModalProps,
    }));
  };

  const hideAndResetModal = () => {
    setState((prevState) => ({
      ...prevState,
      showModal: false,
      currentModal: null,
      optionalModalProps: null,
    }));
  };

  const handlePrevPathUpdate = (path) => {
    setState((prevState) => ({
      ...prevState,
      prevPath: path,
    }));
  };

  const setEULAtrue = () => {
    setState((prevState) => ({
      ...prevState,
      isEULAtrue: true,
    }));
  };

  const updateOnboardingConfirmationCodeStatus = (status) => {
    setState((prevState) => ({
      ...prevState,
      confirmOnboardingPhoneNumber: status,
    }));
  };

  // could be used as handleUserSignOut
  const handleUserSignOut = (explicitPostLogoutPath) => {
    const authUserForRedirect =
      user?.id || user?.user_id
        ? user
        : cookies.get("authUser", { path: "/", secure: true }) || {};
    const targetPath =
      typeof explicitPostLogoutPath === "string" && explicitPostLogoutPath
        ? explicitPostLogoutPath
        : getPostSignOutLoginPath(authUserForRedirect);

    API.beginFreshSession();

    COOKIE_KEYS.forEach((key) =>
      cookies.remove(key, { path: "/", secure: true })
    );
    handleUpdateAuthState({
      user: {},
      isLoggedIn: false,
    });

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(MG_PAYMENT_PORTAL_SESSION_KEY);
    }

    navigate(targetPath, { replace: true });
  };

  const handleUserLogin = (user) => {
    return new Promise((resolve, reject) => {
      // const { cookies } = props;
      const plusOneYear = new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      );
      const cookieOpts = { path: "/", expires: plusOneYear, secure: true };
      if (user?.multi_internal_contacts) {
        delete user.multi_internal_contacts;
      }

      const isPaymentPortalLogin =
        window.location.pathname === MG_CONSUMER_LOGIN_ROUTE ||
        user?.from_payment_portal;

      if (isPaymentPortalLogin) {
        API.beginFreshSession();
      }

      // Prefer OAuth cookies already written by onTokenUpdate (/oauth/token).
      // Session responses (e.g. Intuit SSO) can return a stale auth_token; writing
      // that over the cookie leaves plain-fetch APIs on the old bearer while
      // oauth2.fetch keeps using the new in-memory token → intermittent 500s.
      const oauthAccessToken = cookies.get("accessToken", {
        path: "/",
        secure: true,
      });
      const oauthRefreshToken = cookies.get("refreshToken", {
        path: "/",
        secure: true,
      });
      const hasOauthPair = Boolean(oauthAccessToken && oauthRefreshToken);
      const accessToken = isPaymentPortalLogin
        ? user?.user_auth_token
        : hasOauthPair
          ? oauthAccessToken
          : user?.user_auth_token || oauthAccessToken;
      const refreshToken = isPaymentPortalLogin
        ? user?.user_refresh_token
        : hasOauthPair
          ? oauthRefreshToken
          : user?.user_refresh_token || oauthRefreshToken;

      if (isPaymentPortalLogin && !accessToken) {
        resolve(false);
        return;
      }

      const loginUser = {
        ...user,
        ...(accessToken ? { user_auth_token: accessToken } : {}),
        ...(refreshToken ? { user_refresh_token: refreshToken } : {}),
      };

      cookies.set("authUser", loginUser, cookieOpts);
      cookies.set("isLoggedIn", true, cookieOpts);

      if (accessToken) {
        cookies.set("accessToken", accessToken, cookieOpts);
      }
      if (refreshToken) {
        cookies.set("refreshToken", refreshToken, cookieOpts);
      }

      if (accessToken && refreshToken) {
        API.initOauth2ClientFromCookies();
      } else if (isPaymentPortalLogin) {
        API.resetOauth2Client();
      }

      if (typeof sessionStorage !== "undefined") {
        if (window.location.pathname === MG_CONSUMER_LOGIN_ROUTE) {
          sessionStorage.setItem(MG_PAYMENT_PORTAL_SESSION_KEY, "1");
        } else {
          sessionStorage.removeItem(MG_PAYMENT_PORTAL_SESSION_KEY);
        }
      }

      // getGoogleAnalyticKey().pageview('login page');  // Uncomment if needed

      handleUpdateAuthState({
        user: {
          ...loginUser,
        },
        isLoggedIn: true,
      });
      getAppConfigInfo();
      resolve(true);
    });
  };

  const getLogoColorsForPayment = () => {
    API.getLogoColorsForPayment()
      .then((response) => {
        localStorage.setItem("themeColors", JSON.stringify(response.data));
        setState({
          ...state,
          appThemeData: response.data,
        });
      })
      .catch((err) => {
        showToast("error", err.message);
      });
  };

  const handleUserAgreementAccept = (user) => {
    return new Promise((resolve, reject) => {
      // const { cookies } = props;
      const plusOneYear = new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      );

      const cookieOpts = { path: "/", expires: plusOneYear, secure: true };
      user.onboarding_complete = true;
      cookies.set("authUser", user, cookieOpts);
      handleUpdateAuthState({
        user: {
          ...user,
        },
      });
      navigate("/onboarding/success");
    });
  };

  const setUserData = (data) => {
    setState((prevState) => ({
      ...prevState,
      userData: data,
    }));
  };
  const updateUserName = (first_name, last_name) => {
    handleUpdateAuthState({
      user: {
        ...user,
        first_name,
        last_name,
      },
    });
  };

  // Updated useEffect for initial loading
  useEffect(() => {
    setGlobalLoading(true);
    const accessToken = cookies.get("accessToken", { path: "/", secure: true });
    const orgDataPromise = getOrganizationData();
    const appDataPromise = getAppData();
    let orgDetailsPromise = Promise.resolve();
    if (
      searchParams.get("email") &&
      searchParams.get("sub_id") &&
      searchParams.get("id_token") &&
      isAuthenticated &&
      accessToken &&
      state.companyData &&
      state.companyData.organization_key
    ) {
      orgDetailsPromise = getOrganizationalDetails();
    }
    Promise.all([orgDataPromise, appDataPromise, orgDetailsPromise])
      .catch((err) => {
        showToast("error", err.message);
      })
      .finally(() => setGlobalLoading(false));
    getLogoColorsForPayment();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (Object?.entries(authState?.user)?.length) {
      getFullUserProfile(authState?.user);
    }

    // Set up event listeners
    Emitter.on("OAUTH_ERROR", handleUserSignOut);
    Emitter.on("ORGINIZATION_DETAILS_CALL", getOrganizationalDetails);

    // Clean up event listeners
    return () => {
      Emitter.off("OAUTH_ERROR", handleUserSignOut);
      Emitter.off("ORGINIZATION_DETAILS_CALL", getOrganizationalDetails);
    };
  }, []);

  const modal = state.showModal ? (
    <ModalConductor
      user={user}
      stripePubKey={state.stripePubKey}
      appName={state.appName}
      appEmail={appEmail}
      currentModal={state.currentModal}
      hideModal={hideAndResetModal}
      updateModalState={handleModalChange}
      optionalProps={state.optionalModalProps}
    />
  ) : null;

  const getCommonProps = (state, pathname) => ({
    appThemeData: state.appThemeData,
    appName: state.appName,
    location: pathname,
    user: user,
    isLoggedIn: isLoggedIn,
    progressPercent: state.progressPercent,
  });

  const getScreenProps = (state, hideAndResetModal, handleModalChange) => ({
    appThemeData: state.appThemeData,
    appName: state.appName,
    appEmail: appEmail,
    user: user,
    hideModal: hideAndResetModal,
    updateModalState: handleModalChange,
  });

  const routes = [
    {
      path: "/client/dashboard",
      Component: ClientsDashboard,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/payments",
      Component: PaymentsDeposit,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/payments/add",
      Component: AddPaymentDeposit,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/payments/detail/:id",
      Component: PaymentDepositDetail,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/payouts",
      Component: Payouts,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/payouts/:id",
      Component: PayoutDetail,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/recurring-approvals",
      Component: RecurringApprovals,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/recurring-history",
      Component: RecurringHistory,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/accounts",
      Component: ClientsAccounts,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/view/conversation/:id",
      Component: ViewConsumerConversation,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/accounts/:id/addressDocument",
      Component: ClientViewAddressDoc,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/pendingAddressUser",
      Component: ViewPendingAddressUser,
      Portal: Layout,
    },
    {
      path: "/client/settings",
      Component: ClientsSettings,
      Portal: Layout,
      isProtected: true,
      requiredRoles: ["client_admin"],
      additionalProps: {
        location,
      },
      props: {
        removePadding: true,
        additionalScreenProps: {
          stripePubKey: state.stripePubKey,
          updateUserLogin: handleUserLogin,
        },
      },
    },
    {
      path: "/client/change-account",
      Component: AccountSwitch,
      Portal: Layout,
      isProtected: true,
      requiredRoles: ["client_admin"],
      props: {
        removePadding: true,
        additionalScreenProps: {
          cookies,
          stripePubKey: state.stripePubKey,
          updateUserLogin: handleUserLogin,
        },
      },
    },
    {
      path: "/client/history",
      Component: ClientsHistory,
      Portal: Layout,
    },
    {
      path: "/client/lobHistory",
      Component: LobHistory,
      Portal: Layout,
    },
    {
      path: "/client/messageHistory/:id",
      Component: MessageHistory,
      Portal: Layout,
    },
    {
      path: "/client/view/chat/:id",
      Component: ViewConsumerChat,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/messageHistory/mail/:id",
      Component: MailchimpHistory,
      Portal: Layout,
      additionalProps: { userData: accountViewData },
    },
    {
      path: "/client/skipTrace",
      Component: SkipTrace,
      Portal: Layout,
      additionalProps: { userData: accountViewData },
    },
    {
      path: "/client/help",
      Component: ClientsHelp,
      Portal: Layout,
    },
    {
      path: "/client/accounts/add",
      Component: ClientsAccountAddEditView,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/accounts/:id",
      Component: ClientAccountView,
      Portal: Layout,
      props: {
        removePadding: true,
        additionalScreenProps: { userData: accountViewData },
      },
    },

    {
      path: "/client/accounts/:id/edit-setting",
      Component: ClientsEditSettings,
      Portal: Layout,
      props: {
        removePadding: true,
        additionalScreenProps: { userData: accountViewData },
      },
    },
    {
      path: "/client/accounts/:id/edit",
      Component: ClientsAccountAddEditView,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/elavon",
      Component: ElavonScreen,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/profile",
      Component: ClientsProfile,
      Portal: Layout,
      props: {
        screen: (
          <ClientsProfile
            {...getScreenProps(state, hideAndResetModal, handleModalChange)}
            getUserProfile={updateUserName}
            stripePubKey={state.stripePubKey}
          />
        ),
      },
    },
    {
      path: "/client/offers",
      Component: ClientsOffers,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/tasks",
      Component: ClientsTasks,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/tasks/:tabID",
      Component: ClientsTasks,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/reports",
      Component: ClientsReports,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/reports/:id",
      Component: ClientsReportView,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/invoices",
      Component: ClientsInvoices,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/invoices/import",
      Component: ImportInvoiceData,
      Portal: Layout,
      additionalProps: { type: "invoice" },
    },
    {
      path: "/client/invoices/:id",
      Component: ClientsInvoicesView,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/payment-plans/:id",
      Component: ClientsPaymentPlanView,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/client/add/invoices",
      Component: ClientsAddInvoices,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/creditMemo/:id",
      Component: ClientsCreditMemoView,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: {
        userData,
        back_Path: state.back_Path,
      },
    },
    {
      path: "/client/add/creditMemo",
      Component: ClientsAddCreditMemo,
      Portal: Layout,
      additionalProps: {
        userData,
        handleStateChange,
      },
    },
    {
      path: "/client/admins",
      Component: ClientsAdmins,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/PendingActions",
      Component: ClientsPendingActions,
      Portal: Layout,
      additionalProps: { userData },
    },
    {
      path: "/client/team",
      Component: ClientsEmployees,
      Portal: Layout,
      isProtected: true,
      requiredRoles: ["client_admin"],
    },
    {
      path: "/client/employees/:id",
      Component: ClientsEmployeeAccountsView,
      Portal: Layout,
      additionalProps: { userData: accountViewData },
    },
    {
      path: "/client/import",
      Component: ImportInvoiceData,
      Portal: Layout,
      additionalProps: { type: "account" },
    },
    {
      path: "/sales_user/payments",
      Component: PaymentsDeposit,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/sales_user/payments/add",
      Component: AddPaymentDeposit,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/sales_user/payments/detail/:id",
      Component: PaymentDepositDetail,
      Portal: Layout,
      props: { removePadding: true },
      additionalProps: { userData },
    },
    {
      path: "/sales_user/profile",
      Component: ClientsProfile,
      Portal: Layout,
      props: {
        screen: (
          <ClientsProfile
            {...getScreenProps(state, hideAndResetModal, handleModalChange)}
            getUserProfile={updateUserName}
            stripePubKey={state.stripePubKey}
          />
        ),
      },
    },
  ];

  const welcomeScreenRoutes = [
    {
      path: "/welcome-screen/payment-portal-preview",
      Component: WelcomeScreenView,
      props: {
        type: "PAYMENT_PORTAL_PREVIEW",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen/payment-preferences",
      Component: WelcomeScreenView,
      props: {
        type: "PAYMENT_PREFERENCES",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen/enable-payment",
      Component: WelcomeScreenView,
      props: {
        type: "SetUp_Payment_Account",
        isEnabledpayment: "SetUp_Payment_Account",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen/elavon_form",
      Component: WelcomeScreenView,
      props: {
        type: "ELAVON_FORM",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen/schedule-go-live-date",
      Component: CustomizeWorkFlow,
      props: {
        type: "GO_LIVE_DATE",
        step: "step10",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/schedule-communications",
      Component: CustomizeWorkFlow,
      props: {
        type: "SCHEDULE_COMMUNICATIONS",
        step: "step9",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/communication-preferences",
      Component: CustomizeWorkFlow,
      props: {
        type: "INVOICE_PREFERENCES",
        step: "step8",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/invoice-branding-and-comms",
      Component: CustomizeWorkFlow,
      props: {
        type: "INVOICE_PREFERENCES",
        step: "step7",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/account-placement-eligibility",
      Component: CustomizeWorkFlow,
      props: {
        type: "CREDIT_TERMS",
        step: "step6",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/default-credit-terms",
      Component: CustomizeWorkFlow,
      props: {
        type: "CREDIT_TERMS",
        step: "step5",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/upload-W9-form",
      Component: CustomizeWorkFlow,
      props: {
        type: "CREDIT_TERMS",
        step: "step4",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/select-payment-portal",
      Component: CustomizeWorkFlow,
      props: {
        type: "CREDIT_TERMS",
        step: "step3",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/add-banking-info",
      Component: CustomizeWorkFlow,
      props: {
        type: "CREDIT_TERMS",
        step: "step2",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/add-billing-details",
      Component: CustomizeWorkFlow,
      props: {
        type: "CREDIT_TERMS",
        step: "",
        handleSignOut: handleUserSignOut,
      },
    },
    {
      path: "/welcome-screen/invite-your-team-members",
      Component: WelcomeScreenView,
      props: {
        type: "INVITE_TEAM_MEMBERS",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen/connect-account-system",
      Component: WelcomeScreenView,
      props: {
        type: "CONNECT_ACCOUNT_SYSTEM",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen/account-preferences-sync-quickbook-data",
      Component: WelcomeScreenView,
      props: {
        type: "SYNC_ACCOUNT",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen/add-organization-details",
      Component: WelcomeScreenView,
      props: {
        type: "ADD_ORG",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
    {
      path: "/welcome-screen",
      Component: WelcomeScreenView,
      props: {
        skipLoginCheck: true,
        type: "",
        handleSignOut: handleUserSignOut,
        updateOnboardingComplete: updateOnboardingComplete,
      },
    },
  ];
  return (
    <>
      <Suspense fallback={<Loader loading={true} />}>
        <Routes>
          <Route
            path='/login'
            element={
              <Login
                appName={state.appName}
                user={user}
                isLoggedIn={isLoggedIn}
                location={window.location.pathname}
                updateUserLogin={handleUserLogin}
                updateModalState={handleModalChange}
                heapEmail={state.heapEmail}
                heapAppId={state.heapAppId}
              />
            }
          />
          <Route
            path={MG_CONSUMER_LOGIN_ROUTE}
            element={
              <ConsumerLogin
                appName={state.appName}
                user={user}
                isLoggedIn={isLoggedIn}
                location={window.location.pathname}
                updateUserLogin={handleUserLogin}
                updateModalState={handleModalChange}
                heapEmail={state.heapEmail}
                heapAppId={state.heapAppId}
              />
            }
          />

          {welcomeScreenRoutes.map(({ path, Component, props = {} }) => (
            <Route
              key={path}
              path={path}
              element={
                <Layout
                  appThemeData={state.appThemeData}
                  appName={state.appName}
                  location={path}
                  user={user}
                  isLoggedIn={isLoggedIn}
                  {...props}
                  screen={
                    <Component
                      stripePubKey={state.stripePubKey}
                      appName={state.appName}
                      appThemeData={state.appThemeData}
                      user={user}
                      navigate={navigate}
                      clientName={state.clientName}
                      location={path}
                      updateModalState={handleModalChange}
                      updateUserLogin={handleUserLogin}
                      {...props}
                    />
                  }
                />
              }
            />
          ))}
          {routes.map(
            ({
              path,
              Component,
              Portal,
              props = {},
              additionalProps = {},
              isProtected = false,
              requiredRoles = [],
            }) => {
              const shouldRedirect =
                isProtected &&
                (isRestrictedFromCompanySettings() ||
                  !hasRequiredRole(requiredRoles));

              return (
                <Route
                  key={path}
                  path={path}
                  element={
                    shouldRedirect ? (
                      <Navigate to='/client/dashboard' replace />
                    ) : (
                      <Portal
                        {...getCommonProps(state, path)}
                        {...props}
                        screen={
                          props.screen || (
                            <Component
                              {...getScreenProps(
                                state,
                                hideAndResetModal,
                                handleModalChange
                              )}
                              {...additionalProps}
                            />
                          )
                        }
                      />
                    )
                  }
                />
              );
            }
          )}

          {/* Marketing Pages */}
          <Route
            path='/privacypolicy'
            element={
              <MakeGoodPolicy
                appName={state.appName}
                appEmail={appEmail}
                location={window.location.pathname}
                companyAddress={state.companyAddress}
                adminEmail={state.adminEmail}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/register'
            element={
              <LandingHeader
                token={queryParams.token}
                orgName={queryParams.org_name}
                setUserData={setUserData}
                updateModalState={handleModalChange}
                intercomAppId={state.intercomAppId}
              />
            }
          />

          <Route
            path='/registration-form'
            element={
              <NewRegistration
                userData={state.userData}
                updateModalState={handleModalChange}
                intercomAppId={state.intercomAppId}
              />
            }
          />

          <Route
            path='/confirm-password'
            element={
              <ConfirmPassword
                userData={state.userData}
                updateModalState={handleModalChange}
                intercomAppId={state.intercomAppId}
              />
            }
          />

          <Route path='/password-success' element={<PasswordSuccess />} />

          <Route
            path='/'
            element={
              <MarketingHomepage
                appName={state.appName}
                companyAddress={state.companyAddress}
                adminEmail={state.adminEmail}
                location={window.location.pathname}
                user={user}
                intercomAppId={state.intercomAppId}
                updateModalState={handleModalChange}
              />
            }
          />

          <Route
            path='/configureApp'
            element={
              <AppConfiguration
                appName={state.appName}
                location={window.location.pathname}
                updateModalState={handleModalChange}
                appData={state.appData}
                isLoggedIn={isLoggedIn}
              />
            }
          />

          <Route
            path='/appThirdPartyConfig'
            element={
              <AppThirdPartyConfig
                appName={state.appName}
                location={window.location.pathname}
                updateModalState={handleModalChange}
              />
            }
          />

          <Route
            path='/contact'
            element={
              <MarketingContact
                appName={state.appName}
                location={window.location.pathname}
                updateModalState={handleModalChange}
              />
            }
          />

          <Route
            path='/privacy'
            element={
              <MarketingPrivacy
                appName={state.appName}
                appEmail={appEmail}
                location={window.location.pathname}
                companyAddress={state.companyAddress}
                adminEmail={state.adminEmail}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/terms'
            element={
              <MarketingTerms
                appName={state.appName}
                appEmail={appEmail}
                companyAddress={state.companyAddress}
                adminEmail={state.adminEmail}
                location={window.location.pathname}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/content'
            element={
              <MarketingContent
                appName={state.appName}
                companyAddress={state.companyAddress}
                adminEmail={state.adminEmail}
              />
            }
          />
          <Route
            path='/landing'
            element={<Landing appName={state.appName} appEmail={appEmail} />}
          />

          {/* Sessions Screens */}

          <Route
            path='/sign-out'
            element={
              <SignOut
                handleSignOut={handleUserSignOut}
                user={user}
                isLoggedIn={isLoggedIn}
                location={window.location.pathname}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route path='/bad-request' element={<BadRequest />} />
          <Route path='/server-error' element={<ServerError />} />
          <Route path='/EmailVerifyError' element={<EmailVerifyError />} />
          <Route
            path='/reset-password'
            element={
              <ResetPassword
                updateModalState={handleModalChange}
                appName={state.appName}
              />
            }
          />

          {/* Sessions Screens -- /choose-password -- Catch Rails user reset url --*/}

          <Route
            path='/users/:id/password/edit'
            element={
              <ChoosePassword
                user={user}
                token={new URLSearchParams(location.search).get("token")}
                appName={state.appName}
                location={location.pathname}
                updateUserLogin={handleUserLogin}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/users/:id/set_password'
            element={
              <ChoosePassword
                user={user}
                token={new URLSearchParams(location.search).get("token")}
                appName={state.appName}
                location={location.pathname}
                updateUserLogin={handleUserLogin}
                updateModalState={handleModalChange}
              />
            }
          />

          {/* Onboarding Screens */}

          <Route
            path='/create-account'
            element={
              <OnboardingCreateAccount
                appName={state.appName}
                updateUserLogin={handleUserLogin}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/confirm-account/phone'
            element={
              <OnboardingConfirmAccountPhone
                appName={state.appName}
                user={user}
                reconfirmPhoneNumber={state.confirmOnboardingPhoneNumber}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/confirm-account/enter-code'
            element={
              <OnboardingConfirmAccountCode
                appName={state.appName}
                user={user}
                handleResendConfirmationCode={
                  updateOnboardingConfirmationCodeStatus
                }
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/onboarding/address'
            element={
              <OnboardingResidence
                appName={state.appName}
                user={user}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/onboarding/verify-identity'
            element={
              <OnboardingVerifyIdentity
                appName={state.appName}
                user={user}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/onboarding/user-agreement'
            element={
              <OnboardingUserAgreement
                appName={state.appName}
                user={user}
                handleSignOut={handleUserSignOut}
                handleAcceptUserAgreement={handleUserAgreementAccept}
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/onboarding/success'
            element={
              <OnboardingCompletion
                appName={state.appName}
                user={user}
                updateModalState={handleModalChange}
              />
            }
          />
          {/* <Route
            path='/welcome-screen/accounts'
            element={
              <ConsumerPortalStatic
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                intercomAppId={state.intercomAppId}
                removePadding={true}
                appThemeData={state.appThemeData}
                handleSignOut={handleUserSignOut}
                screen={
                  <ConsumerAccountsInvoices
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    user={user}
                    objectListData={userDashboardObjectLists}
                    updateModalState={handleModalChange}
                    handleSignOut={handleUserSignOut}
                  />
                }
              />
            }
          /> */}

          {/* User Dashboard Screens */}

          {/* Redirects */}
          <Route
            path='/dashboard'
            element={<Navigate to='/dashboard/accounts/invoices' />}
          />
          <Route
            path='/dashboard/accounts'
            element={<Navigate to='/dashboard/accounts/invoices' />}
          />

          {/* Consumer Portal Route */}
          {/* /dashboard/accounts/invoices */}
          <Route
            path='/dashboard/accounts/:tab'
            element={
              <ConsumerPortal
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                intercomAppId={state.intercomAppId}
                removePadding={true}
                appThemeData={state.appThemeData}
                screen={
                  <ConsumerAccounts
                    appName={state.appName}
                    user={user}
                    appThemeData={state.appThemeData}
                    objectListData={userDashboardObjectLists}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Pay Invoices Route */}
          <Route
            path='/dashboard/accounts/invoices/pay-invoices'
            element={
              <ConsumerPortal
                appThemeData={state.appThemeData}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                removePadding={true}
                prevPath='/dashboard/accounts/invoices'
                screen={
                  <PayInvoices
                    primary_color={state.appThemeData.primary_color}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    stripePubKey={state.stripePubKey}
                    updatePrevPath={handlePrevPathUpdate}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Consumer Accounts View Route */}
          <Route
            path='/dashboard/accounts/invoices/:id'
            element={
              <ConsumerPortal
                appThemeData={state.appThemeData}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                removePadding={true}
                prevPath='/dashboard/accounts/invoices'
                screen={
                  <ConsumerAccountsView
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    stripePubKey={state.stripePubKey}
                    updatePrevPath={handlePrevPathUpdate}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Consumer Payment Plan Route */}
          <Route
            path='/dashboard/accounts/payment-plans/:id'
            element={
              <ConsumerPortal
                appThemeData={state.appThemeData}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                removePadding={true}
                prevPath='/dashboard/accounts/payment-plans'
                screen={
                  <ConsumerPaymentPlanDetails
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    stripePubKey={state.stripePubKey}
                    updatePrevPath={handlePrevPathUpdate}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Pay Payment Plans Route */}
          <Route
            path='/dashboard/accounts/pay-payment-plans/:id'
            element={
              <ConsumerPortal
                appThemeData={state.appThemeData}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                removePadding={true}
                prevPath='/dashboard/accounts/payment-plans'
                screen={
                  <PayPaymentPlans
                    primary_color={state.appThemeData.primary_color}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    stripePubKey={state.stripePubKey}
                    updatePrevPath={handlePrevPathUpdate}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Review Payment Plans Route */}
          <Route
            path='/dashboard/accounts/payment-plans/:id/review'
            element={
              <ConsumerPortal
                appThemeData={state.appThemeData}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                removePadding={true}
                prevPath='/dashboard/accounts/payment-plans'
                screen={
                  <ConsumerAccountsPaymentplansReview
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    stripePubKey={state.stripePubKey}
                    updatePrevPath={handlePrevPathUpdate}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Static Account Route */}
          <Route
            path='/welcome-screen/accounts/000000'
            element={
              <ConsumerPortalStatic
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                removePadding={true}
                prevPath='/welcome-screen/accounts'
                screen={
                  <ConsumerAccountsViewStatic
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    stripePubKey={state.stripePubKey}
                    updatePrevPath={handlePrevPathUpdate}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Document Validate Route */}
          <Route
            path='/dashboard/accounts/:id/document-validate'
            element={
              <ConsumerPortal
                appName={state.appName}
                location={location.pathname}
                user={user}
                headerTab='Accounts'
                isLoggedIn={isLoggedIn}
                prevPath='/dashboard/accounts/:id'
                screen={
                  <ConsumerDocumentValidate
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    updatePrevPath={handlePrevPathUpdate}
                    objectListData={userDashboardObjectLists}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Negotiate Route */}
          <Route
            path='/dashboard/accounts/:id/negotiate'
            element={
              <ConsumerPortal
                appName={state.appName}
                location={location.pathname}
                user={user}
                headerTab='Accounts'
                isLoggedIn={isLoggedIn}
                prevPath='/dashboard/accounts'
                screen={
                  <ConsumerAccountsPaymentNegotiateSelection
                    appName={state.appName}
                    user={user}
                    updatePrevPath={handlePrevPathUpdate}
                    objectListData={userDashboardObjectLists}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Validate Route */}
          <Route
            path='/dashboard/accounts/:id/validate'
            element={
              <ConsumerPortal
                appName={state.appName}
                location={location.pathname}
                user={user}
                headerTab='Accounts'
                isLoggedIn={isLoggedIn}
                prevPath='/dashboard/accounts'
                screen={
                  <ConsumerAccountsPaymentValidate
                    appName={state.appName}
                    user={user}
                    updatePrevPath={handlePrevPathUpdate}
                    objectListData={userDashboardObjectLists}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* Negotiate Payment Plan Route */}
          <Route
            path='/dashboard/accounts/:id/negotiate/payment-plan'
            element={
              state.stripePubKey !== null ? (
                <ConsumerPortal
                  appName={state.appName}
                  location={location.pathname}
                  user={user}
                  headerTab='Accounts'
                  isLoggedIn={isLoggedIn}
                  screen={
                    <ConsumerAccountsNegotiatePaymentPlan
                      appName={state.appName}
                      user={user}
                      updatePrevPath={handlePrevPathUpdate}
                      objectListData={userDashboardObjectLists}
                      hideModal={hideAndResetModal}
                      updateModalState={handleModalChange}
                      stripePubKey={state.stripePubKey}
                    />
                  }
                />
              ) : (
                <div />
              )
            }
          />

          {/* Negotiate One-Time Payment Route */}
          <Route
            path='/dashboard/accounts/:id/negotiate/one-time-payment'
            element={
              state.stripePubKey !== null ? (
                <ConsumerPortal
                  appName={state.appName}
                  location={location.pathname}
                  user={user}
                  headerTab='Accounts'
                  isLoggedIn={isLoggedIn}
                  screen={
                    <ConsumerAccountsNegotiateOneTimePayment
                      appName={state.appName}
                      user={user}
                      updatePrevPath={handlePrevPathUpdate}
                      objectListData={userDashboardObjectLists}
                      hideModal={hideAndResetModal}
                      updateModalState={handleModalChange}
                      stripePubKey={state.stripePubKey}
                    />
                  }
                />
              ) : (
                <div />
              )
            }
          />

          {/* Settle Account Route */}
          <Route
            path='/dashboard/accounts/:id/settle-account'
            element={
              state.stripePubKey !== null ? (
                <ConsumerPortal
                  appName={state.appName}
                  location={location.pathname}
                  user={user}
                  headerTab='Accounts'
                  isLoggedIn={isLoggedIn}
                  prevPath='/dashboard/accounts'
                  screen={
                    <ConsumerAccountsPaymentSettleAccount
                      appName={state.appName}
                      user={user}
                      updatePrevPath={handlePrevPathUpdate}
                      objectListData={userDashboardObjectLists}
                      hideModal={hideAndResetModal}
                      updateModalState={handleModalChange}
                      stripePubKey={state.stripePubKey}
                    />
                  }
                />
              ) : (
                <div />
              )
            }
          />

          {/* Profile Route */}
          <Route
            path='/dashboard/profile'
            element={
              state.stripePubKey !== null ? (
                <ConsumerPortal
                  appName={state.appName}
                  location={location.pathname}
                  user={user}
                  removePadding={true}
                  isLoggedIn={isLoggedIn}
                  appThemeData={state.appThemeData}
                  screen={
                    <ConsumerProfile
                      appName={state.appName}
                      hideModal={hideAndResetModal}
                      updateModalState={handleModalChange}
                      user={user}
                      stripePubKey={state.stripePubKey}
                      appThemeData={state.appThemeData}
                    />
                  }
                />
              ) : (
                <div />
              )
            }
          />

          {/* Help Route */}
          <Route
            path='/dashboard/help'
            element={
              <ConsumerPortal
                appName={state.appName}
                location={location.pathname}
                user={user}
                removePadding={true}
                isLoggedIn={isLoggedIn}
                screen={
                  <ConsumerHelp
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/super-admin'
            element={<Navigate to='/super-admin/accounts' replace />} // Redirecting from /super-admin to /super-admin/accounts
          />
          <Route
            path='/super-admin/accounts'
            element={
              <SuperAdminPortal
                location='/super-admin/accounts'
                user={user}
                appThemeData={state.appThemeData}
                isLoggedIn={isLoggedIn}
                screen={
                  <SuperAdminAccounts
                    appName={state.appName}
                    user={user}
                    appThemeData={state.appThemeData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/super-admin/profile'
            element={
              <SuperAdminPortal
                location='/super-admin/profile'
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <SuperAdminProfile
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/super-admin/accounts/:id'
            element={
              <SuperAdminPortal
                location='/super-admin/accounts/:id' // Directly use the path
                user={user}
                appThemeData={state.appThemeData}
                isLoggedIn={isLoggedIn}
                screen={
                  <SuperAdminAccountsView
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    appThemeData={state.appThemeData}
                    userData={accountViewData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/super-admin/accounts/:id/edit'
            element={
              <SuperAdminPortal
                appName={state.appName}
                location='/super-admin/accounts/:id/edit' // Directly use the path
                user={user}
                appThemeData={state.appThemeData}
                isLoggedIn={isLoggedIn}
                screen={
                  <SuperAdminAccountAddEditView
                    user={user}
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    userData={userData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/employee'
            element={<Navigate to='/employee/accounts' replace />} // Redirect to /employee/accounts
          />
          <Route
            path='/employee/accounts'
            element={
              <EmployeePortal
                location='/employee/accounts' // Directly use the path
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeAccounts
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/employee/accounts/:id'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/accounts/:id' // Directly use the path
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeAccountsView
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    userData={accountViewData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/accounts/:id/edit'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/accounts/:id/edit' // Directly use the path
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeAccountAddEditView
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/view/conversation/:id'
            element={
              <EmployeePortal
                appName={state.appName}
                appThemeData={state.appThemeData}
                location='/employee/view/conversation/:id' // Directly use the path
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <ViewConsumerConversation
                    user={user}
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/employee/accounts/:id/addressDocument'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/accounts/:id/addressDocument' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeViewAddressDoc
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/profile'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/profile' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  state.stripePubKey !== null ? (
                    <EmployeeProfile
                      appName={state.appName}
                      hideModal={hideAndResetModal}
                      updateModalState={handleModalChange}
                      user={user}
                      stripePubKey={state.stripePubKey}
                    />
                  ) : (
                    <div />
                  )
                }
              />
            }
          />
          <Route
            path='/employee/offers'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/offers' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeOffers
                    user={user}
                    appName={state.appName}
                    appEmail={appEmail}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/employee/skipPayment'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/skipPayment' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <SkipPayment
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/paymentHistory'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/paymentHistory' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeePaymentHistory
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/lobHistory'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/lobHistory' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeLobHistory
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          <Route
            path='/employee/messageHistory/:id'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/messageHistory/:id' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeMessageHistory
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/view/chat/:id'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/view/chat/:id' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeViewConsumerChat
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/messageHistory/mail/:id'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/messageHistory/mail/:id' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeMailchimpHistory
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    userData={accountViewData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            path='/employee/skipTrace'
            element={
              <EmployeePortal
                appName={state.appName}
                location='/employee/skipTrace' // Hardcoded location
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <EmployeeSkipTrace
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    userData={accountViewData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />

          {/* QuickBook login flow pages */}

          <Route
            path='/qb-success'
            element={
              <QbSuccess
                appName={state.appName}
                location='/qb-success' // Hardcoded location
              />
            }
          />
          <Route
            path='/qb-disconnect'
            element={
              <QbDisconnect
                appName={state.appName}
                location='/qb-disconnect' // Hardcoded location
              />
            }
          />
          <Route
            path='/qb-failure'
            element={
              <QbFailure
                appName={state.appName}
                location='/qb-failure' // Hardcoded location
                updateUserLogin={handleUserLogin}
              />
            }
          />

          <Route
            path='/payment'
            element={
              <InvoiceAmountForm
                appThemeData={state.appThemeData}
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/payment' // Hardcoded location
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/deposits/*'
            element={
              <DepositExpress
                appThemeData={state.appThemeData}
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/deposits'
                updateModalState={handleModalChange}
                user={user}
              />
            }
          />
          <Route
            path='/welcome-screen/payment'
            element={
              <InvoiceAmountFormStatic
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/welcome-screen/payment' // Hardcoded location
                updateModalState={handleModalChange}
                handleSignOut={handleUserSignOut}
              />
            }
          />

          <Route
            path='/payment-success'
            element={
              <ExpressPaymentSuccess
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/payment-success' // Hardcoded location
                invoiceNumber={
                  queryString.parse(window.location.search).invoice_number
                }
                amount={queryString.parse(window.location.search).amount}
                updateModalState={handleModalChange}
              />
            }
          />

          <Route
            path='/payment-failed'
            element={
              <ExpressPaymentFailed
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/payment-failed' // Hardcoded location
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/enable-payment/success'
            element={
              <StripePaymentSuccess
                appName={state.appName}
                stripePubKey={state.stripePubKey}
                location='/enable-payment/success'
                user={user}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path='/enable-payment/failed'
            element={
              <StripePaymentFailed
                user={user}
                isLoggedIn={isLoggedIn}
                appName={state.appName}
                location='/enable-payment/failed' // Hardcoded location
              />
            }
          />
          <Route
            path='/help'
            element={
              <ConsumerGetHelp
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/help' // Hardcoded location
                updateModalState={handleModalChange}
              />
            }
          />

          <Route
            path='/sso-success'
            element={
              <SSOSuccess
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/sso-success' // Hardcoded location
                updateModalState={handleModalChange}
                updateUserLogin={handleUserLogin}
              />
            }
          />
          <Route
            path='/sso_failure'
            element={
              <SSOFailure
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/sso_failure' // Hardcoded location
                updateModalState={handleModalChange}
              />
            }
          />
          <Route
            path='/sso_dashboard'
            element={
              <ClientsSSODashboard
                user={user}
                isEULAtrue={state.isEULAtrue}
                setEULAtrue={setEULAtrue}
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                clientName={state.clientName}
                location='/sso_dashboard' // Hardcoded location
                updateModalState={handleModalChange}
                handleSignOut={handleUserSignOut}
                updateUserLogin={handleUserLogin}
              />
            }
          />
          <Route
            path='/eula'
            element={
              <MakeGoodEULA
                isEULAtrue={state.isEULAtrue}
                setEULAtrue={setEULAtrue}
                location='/eula' // Hardcoded location
                updateModalState={handleModalChange}
                handleSignOut={handleUserSignOut}
                user={{
                  user_auth_token: user.auth_token,
                  user_id: user.user_id,
                  eula_accepted_date: user.accepted_on,
                }}
              />
            }
          />

          <Route
            path='/sub-domain'
            element={
              <CreateSubDomain
                stripePubKey={state.stripePubKey}
                appName={state.appName}
                user={user}
                clientName={state.clientName}
                location='/sub-domain' // Hardcoded location
                updateModalState={handleModalChange}
              />
            }
          />

          <Route
            path='/welcome-screen/login'
            element={
              <PaymentPortalLogin
                appName={state.appName}
                user={user}
                isLoggedIn={state.isLoggedIn}
                location='/welcome-screen/login' // Hardcoded location
                updateUserLogin={handleUserLogin}
                updateModalState={handleModalChange}
                heapEmail={state.heapEmail}
                heapAppId={state.heapAppId}
                handleSignOut={handleUserSignOut}
              />
            }
          />

          <Route
            path='/welcome-screen/accounts'
            element={
              <ConsumerPortalStatic
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={state.isLoggedIn}
                intercomAppId={state.intercomAppId}
                removePadding={true}
                appThemeData={state.appThemeData}
                handleSignOut={handleUserSignOut}
                screen={
                  <ConsumerAccountsInvoices
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    user={user}
                    objectListData={userDashboardObjectLists}
                    updateModalState={handleModalChange}
                    handleSignOut={handleUserSignOut}
                  />
                }
              />
            }
          />
          <Route
            path='/team_registration'
            element={<UserData updateModalState={handleModalChange} />}
          />
          {/* Admin Routes */}
          <Route path='/admin' element={<Navigate to='/admin/accounts' />} />
          {/* <Navigate exact from="/admin" to="/admin/accounts" push /> */}
          <Route
            exact
            path='/admin/accounts'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAccounts
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/accounts/add'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAccountAddEditView
                    {...props}
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/accounts/:id'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                appThemeData={state.appThemeData}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAccountsView
                    {...props}
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    userData={accountViewData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/accounts/:id/edit'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAccountAddEditView
                    {...props}
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/offers'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminOffers
                    user={user}
                    appName={state.appName}
                    appEmail={appEmail}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/admins'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAdmins
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/PendingActions'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminPendingActions
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/companyDashboard'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <CompanyAccounts
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/companyDashboard/add'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAccountAddEditView
                    {...props}
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/companyDashboard/:id'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                appThemeData={state.appThemeData}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAccountsView
                    {...props}
                    appName={state.appName}
                    appEmail={appEmail}
                    user={user}
                    appThemeData={state.appThemeData}
                    userData={accountViewData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/companyDashboard/:id/edit'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminAccountAddEditView
                    {...props}
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/view/conversation/:id'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                appThemeData={state.appThemeData}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <ViewConsumerConversation
                    {...props}
                    user={user}
                    appThemeData={state.appThemeData}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/dashboard/profile/debtdetail'
            element={
              <ConsumerPortal
                appName={state.appName}
                location={location.pathname}
                user={user}
                headerTab={"Debt Detail"}
                isLoggedIn={isLoggedIn}
                screen={
                  <DebtDetail
                    {...props}
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/accounts/:id/addressDocument'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <AdminViewAddressDoc
                    {...props}
                    user={user}
                    appName={state.appName}
                    userData={userData}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
          <Route
            exact
            path='/admin/pendingAddressUser'
            element={
              <AdminPortal
                {...props}
                appName={state.appName}
                location={location.pathname}
                user={user}
                isLoggedIn={isLoggedIn}
                screen={
                  <ViewPendingAddressUser
                    appName={state.appName}
                    user={user}
                    hideModal={hideAndResetModal}
                    updateModalState={handleModalChange}
                  />
                }
              />
            }
          />
        </Routes>
      </Suspense>
      {modal}
      {state.showModal &&
      !(state.currentModal === "SUCCESS" || state.currentModal === "ERROR") ? (
        <div className='curtain'></div>
      ) : null}
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={1}
      />
      {globalLoading && <Loader loading={true} />}
    </>
  );
};

export default AppRouter;
